import { useState, useEffect } from 'react';
import { X, Download, TrendingUp, TrendingDown } from 'lucide-react';
import {
  performanceService,
  MetricDrilldown,
  TimeWindow,
  AggregationLevel,
} from '../../services/performance.service';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface MetricDrilldownModalProps {
  metricName: string;
  organizationId: string;
  aggregationLevel: AggregationLevel;
  timeWindow: TimeWindow;
  entityId?: string;
  customStart?: string;
  customEnd?: string;
  onClose: () => void;
}

export default function MetricDrilldownModal({
  metricName,
  organizationId,
  aggregationLevel,
  timeWindow,
  entityId,
  customStart,
  customEnd,
  onClose,
}: MetricDrilldownModalProps) {
  const [drilldown, setDrilldown] = useState<MetricDrilldown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  useEffect(() => {
    loadDrilldown();
  }, [metricName, organizationId, aggregationLevel, timeWindow, entityId, customStart, customEnd]);

  const loadDrilldown = async () => {
    setIsLoading(true);
    try {
      const data = await performanceService.getMetricDrilldown(
        organizationId,
        metricName,
        aggregationLevel,
        timeWindow,
        entityId,
        customStart,
        customEnd
      );
      setDrilldown(data);
    } catch (error) {
      console.error('Failed to load drilldown data:', error);
      // Use demo data instead of showing error
      setDrilldown(getDemoDrilldown(metricName, timeWindow));
    } finally {
      setIsLoading(false);
    }
  };

  const getDemoDrilldown = (metric: string, window: TimeWindow): MetricDrilldown => {
    const baseDate = new Date('2026-01-08');
    const dataPoints = window === 'today' || window === 'yesterday' ? 24 : 7;

    const data = [];
    for (let i = 0; i < dataPoints; i++) {
      const timestamp = new Date(baseDate);
      if (window === 'today' || window === 'yesterday') {
        timestamp.setHours(i);
      } else {
        timestamp.setDate(baseDate.getDate() + i);
      }

      // Generate realistic values based on metric type
      let value = 0;
      switch (metric) {
        case 'wfmAdherence':
          value = 85 + Math.random() * 10 + Math.sin(i / 3) * 3;
          break;
        case 'occupancy':
          value = 75 + Math.random() * 10 + Math.sin(i / 2) * 5;
          break;
        case 'productivity':
          value = 11 + Math.random() * 3 + Math.cos(i / 2) * 1;
          break;
        case 'csat':
          value = 4.0 + Math.random() * 0.5 + Math.sin(i / 4) * 0.2;
          break;
        case 'sla':
          value = 88 + Math.random() * 8 + Math.cos(i / 3) * 3;
          break;
        case 'costPerContact':
          value = 4.0 + Math.random() * 1.0 + Math.sin(i / 3) * 0.3;
          break;
        case 'qaRating':
          value = 85 + Math.random() * 8 + Math.cos(i / 4) * 3;
          break;
        default:
          value = 80 + Math.random() * 15;
      }

      data.push({
        timestamp: timestamp.toISOString(),
        value: parseFloat(value.toFixed(2)),
        details: {
          sample: true,
          period: window === 'today' || window === 'yesterday' ? `Hour ${i}` : `Day ${i + 1}`,
        },
      });
    }

    return {
      metricName: metric,
      timeWindow: {
        start: baseDate.toISOString(),
        end: new Date(baseDate.getTime() + dataPoints * (window === 'today' || window === 'yesterday' ? 3600000 : 86400000)).toISOString(),
      },
      aggregationLevel,
      entityId,
      data,
    };
  };

  const handleExportCSV = () => {
    if (!drilldown) return;

    const rows: string[] = [];
    rows.push(`${getMetricTitle()} - Detailed Data`);
    rows.push(`Generated: ${new Date().toLocaleString()}`);
    rows.push(`Time Window: ${drilldown.timeWindow.start} to ${drilldown.timeWindow.end}`);
    rows.push('');
    rows.push('Timestamp,Value');

    drilldown.data.forEach((item) => {
      rows.push(`${item.timestamp},${item.value.toFixed(2)}`);
    });

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${metricName}-drilldown-${Date.now()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getMetricTitle = () => {
    const titles: Record<string, string> = {
      wfmAdherence: 'WFM Adherence',
      occupancy: 'Occupancy',
      productivity: 'Productivity (Solves/Hour)',
      csat: 'CSAT Score',
      sla: 'SLA Compliance',
      costPerContact: 'Cost per Contact',
      qaRating: 'QA Rating',
    };
    return titles[metricName] || metricName;
  };

  const formatChartData = () => {
    if (!drilldown) return [];

    return drilldown.data.map((item) => ({
      timestamp: new Date(item.timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: item.timestamp.includes('T') ? 'numeric' : undefined,
      }),
      value: item.value,
      fullTimestamp: item.timestamp,
    }));
  };

  const calculateStats = () => {
    if (!drilldown || drilldown.data.length === 0) {
      return { avg: 0, min: 0, max: 0, trend: 0 };
    }

    const values = drilldown.data.map((d) => d.value);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Calculate trend (last value vs first value)
    const firstValue = values[0];
    const lastValue = values[values.length - 1];
    const trend = ((lastValue - firstValue) / firstValue) * 100;

    return { avg, min, max, trend };
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            <span className="text-gray-600">Loading detailed data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!drilldown) {
    return null;
  }

  const chartData = formatChartData();
  const stats = calculateStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{getMetricTitle()}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Detailed breakdown • {aggregationLevel} level
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-600 mb-1">Average</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avg.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-600 mb-1">Minimum</p>
              <p className="text-2xl font-bold text-gray-900">{stats.min.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-600 mb-1">Maximum</p>
              <p className="text-2xl font-bold text-gray-900">{stats.max.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-600 mb-1">Trend</p>
              <div className="flex items-center space-x-2">
                {stats.trend >= 0 ? (
                  <TrendingUp className="h-5 w-5 text-green-600" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-red-600" />
                )}
                <p className={`text-2xl font-bold ${stats.trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(stats.trend).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Controls */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Chart Type:</span>
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1 text-sm rounded ${
                chartType === 'line'
                  ? 'bg-purple-100 text-purple-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1 text-sm rounded ${
                chartType === 'bar'
                  ? 'bg-purple-100 text-purple-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Bar
            </button>
          </div>
          <span className="text-sm text-gray-600">
            {drilldown.data.length} data points
          </span>
        </div>

        {/* Chart */}
        <div className="p-6">
          <ResponsiveContainer width="100%" height={400}>
            {chartType === 'line' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#9333ea"
                  strokeWidth={2}
                  dot={{ fill: '#9333ea', r: 4 }}
                  activeDot={{ r: 6 }}
                  name={getMetricTitle()}
                />
              </LineChart>
            ) : (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                  }}
                />
                <Legend />
                <Bar dataKey="value" fill="#9333ea" name={getMetricTitle()} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Data Table */}
        <div className="p-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Raw Data</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {drilldown.data.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(item.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.value.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.details ? (
                        <pre className="text-xs">{JSON.stringify(item.details, null, 2)}</pre>
                      ) : (
                        <span className="text-gray-400">No additional details</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
